import { useState } from 'react'
import { router } from '@inertiajs/react'
import PlatformLayout from '@/components/PlatformLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  CalendarClock,
  CheckCircle2,
  Lock,
  Undo2,
  Wallet,
  TrendingUp,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'

// ── Helpers ──

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatDate(value: string | null, pattern = 'dd/MM/yyyy'): string {
  if (!value) return '-'
  return format(parseISO(value), pattern, { locale: ptBR })
}

// ── Types ──

interface Period {
  reference: string
  start: string
  end: string
  payout_date: string
  label: string
}

interface PeriodOption {
  value: string
  label: string
}

interface Row {
  school_id: number
  school_name: string
  payout_id: number | null
  status: 'open' | 'closed' | 'paid'
  gross_amount: number
  fees_amount: number
  net_amount: number
  transactions_count: number
  paid_at: string | null
  margin_amount: number
  margin_partial: boolean
}

interface Totals {
  schools_count: number
  gross_amount: number
  net_amount: number
  margin_amount: number
  to_pay_amount: number
  paid_amount: number
}

interface Props {
  period: Period
  periods: PeriodOption[]
  rows: Row[]
  totals: Totals
}

// ── Sub-componentes ──

function StatusBadge({ row }: { row: Row }) {
  if (row.status === 'paid') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-green-700 dark:text-green-500">
        <CheckCircle2 className="h-3.5 w-3.5" /> Pago {formatDate(row.paid_at)}
      </span>
    )
  }

  if (row.status === 'closed') {
    return (
      <Badge variant="outline" className="gap-1.5">
        <Lock className="h-3 w-3" /> A pagar
      </Badge>
    )
  }

  return (
    <Badge variant="secondary" className="gap-1.5">
      <CalendarClock className="h-3 w-3" /> Quinzena aberta
    </Badge>
  )
}

/** Baixa do pagamento: pede a data em que a transferência saiu de fato. */
function MarkPaidDialog({
  row,
  onClose,
}: {
  row: Row | null
  onClose: () => void
}) {
  const [paidAt, setPaidAt] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [saving, setSaving] = useState(false)

  if (!row) return null

  function confirm() {
    if (!row?.payout_id) return

    setSaving(true)
    router.patch(
      `/platform/payouts/${row.payout_id}/mark_paid`,
      { paid_at: paidAt },
      {
        onFinish: () => {
          setSaving(false)
          onClose()
        },
      },
    )
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Dar baixa no repasse</DialogTitle>
          <DialogDescription>
            {row.school_name} — {formatCurrency(row.net_amount)} em{' '}
            {row.transactions_count} venda(s).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label htmlFor="paid-at" className="text-sm font-medium">
            Data da transferência
          </label>
          <Input
            id="paid-at"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Registra que o PIX/TED já saiu no ASAAS. Não dispara transferência
            nenhuma — a baixa é só o registro.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={confirm} disabled={saving}>
            {saving ? 'Salvando...' : 'Confirmar baixa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Página ──

export default function Index({ period, periods, rows, totals }: Props) {
  const [marking, setMarking] = useState<Row | null>(null)
  const hasOpen = rows.some((r) => r.status === 'open')

  function selectPeriod(value: string) {
    router.get('/platform/payouts', { period: value }, { preserveScroll: true })
  }

  function closePeriod() {
    router.post(
      '/platform/payouts/close',
      { period: period.reference },
      { preserveScroll: true },
    )
  }

  function reopen(row: Row) {
    if (!row.payout_id) return
    router.patch(
      `/platform/payouts/${row.payout_id}/reopen`,
      {},
      { preserveScroll: true },
    )
  }

  return (
    <PlatformLayout>
      <div className="p-6 space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Repasses</h1>
            <p className="text-sm text-muted-foreground">
              O que a BeWave deve a cada escola na quinzena, e a baixa quando a
              transferência sai.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={period.end}
              onChange={(e) => selectPeriod(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              aria-label="Quinzena"
            >
              {periods.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>

            {hasOpen && (
              <Button variant="outline" onClick={closePeriod}>
                <Lock className="h-4 w-4 mr-2" /> Fechar quinzena
              </Button>
            )}
          </div>
        </div>

        {/* Pagamento previsto da quinzena selecionada */}
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
          <CalendarClock className="h-4 w-4 flex-shrink-0" />
          <span>
            Quinzena{' '}
            <span className="font-medium text-foreground">{period.label}</span>{' '}
            — pagamento previsto para{' '}
            <span className="font-medium text-foreground">
              {formatDate(period.payout_date)}
            </span>{' '}
            (15 dias após o fechamento).
          </span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              label: 'Escolas com venda',
              value: String(totals.schools_count),
              icon: Wallet,
            },
            {
              label: 'A pagar',
              value: formatCurrency(totals.to_pay_amount),
              icon: CalendarClock,
            },
            {
              label: 'Já pago',
              value: formatCurrency(totals.paid_amount),
              icon: CheckCircle2,
            },
            {
              label: 'Margem BeWave',
              value: formatCurrency(totals.margin_amount),
              icon: TrendingUp,
            },
          ].map((stat) => (
            <div key={stat.label} className="rounded-lg border p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <stat.icon className="h-4 w-4" />
                <p className="text-xs">{stat.label}</p>
              </div>
              <p className="text-xl font-bold tracking-tight mt-1">
                {stat.value}
              </p>
            </div>
          ))}
        </div>

        {rows.length === 0 ? (
          <div className="rounded-lg border border-dashed p-10 text-center">
            <p className="text-sm text-muted-foreground">
              Nenhuma escola teve venda pelo app nesta quinzena.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Escola</TableHead>
                  <TableHead className="text-right">Vendas</TableHead>
                  <TableHead className="text-right">Bruto</TableHead>
                  <TableHead className="text-right">Taxas</TableHead>
                  <TableHead className="text-right">A repassar</TableHead>
                  <TableHead className="text-right">Margem BeWave</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.school_id}>
                    <TableCell className="font-medium">
                      {row.school_name}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.transactions_count}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {formatCurrency(row.gross_amount)}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap text-muted-foreground">
                      -{formatCurrency(row.fees_amount)}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap font-semibold">
                      {formatCurrency(row.net_amount)}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <span
                        className={
                          row.margin_amount < 0
                            ? 'text-red-600 font-medium'
                            : 'text-green-700 dark:text-green-500'
                        }
                      >
                        {formatCurrency(row.margin_amount)}
                      </span>
                      {row.margin_partial && (
                        <span
                          className="block text-xs text-muted-foreground"
                          title="Nem todo pagamento tem o valor líquido do ASAAS registrado"
                        >
                          parcial
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <StatusBadge row={row} />
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {row.status === 'closed' && (
                        <Button size="sm" onClick={() => setMarking(row)}>
                          Dar baixa
                        </Button>
                      )}
                      {row.status === 'paid' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => reopen(row)}
                        >
                          <Undo2 className="h-4 w-4 mr-1.5" /> Desfazer
                        </Button>
                      )}
                      {row.status === 'open' && (
                        <span className="text-xs text-muted-foreground">
                          feche a quinzena
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <MarkPaidDialog row={marking} onClose={() => setMarking(null)} />
    </PlatformLayout>
  )
}
